import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { copy } from "../copy";
import Icon from "../components/Icon";
import SiteFooter from "../components/SiteFooter";
import { api, type Inbox, type Me } from "../dashboard/api";
import DashboardTabs, { type DashboardTab } from "../dashboard/DashboardTabs";
import InboxTab from "../dashboard/InboxTab";
import PhotoTab from "../dashboard/PhotoTab";
import ProfileTab from "../dashboard/ProfileTab";
import Sidebar from "../dashboard/Sidebar";
import WithdrawalsTab from "../dashboard/WithdrawalsTab";
import { useSession, useSessionReady } from "../session";

export const DASHBOARD_LOGIN = `/login?next=${encodeURIComponent("/dashboard")}`;

// The account tabs get the credits rail; the wide tools do not.
const RAIL_TABS: DashboardTab[] = ["profile", "photo"];

type State = { status: "loading" } | { status: "error" } | { status: "ready"; me: Me; inbox: Inbox };

// A signed-in producer's account. Every figure comes from /v1/web/me*; a
// visitor is sent to log in before anything under it is requested.
export default function Dashboard() {
  const session = useSession();
  const ready = useSessionReady();
  const member = session.kind !== "visitor";
  const t = copy.dashboard;
  const [state, setState] = useState<State>({ status: "loading" });
  const [tab, setTab] = useState<DashboardTab>("profile");

  useEffect(() => {
    document.title = t.title;
  }, [t.title]);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    const [me, inbox] = await Promise.all([api.me(), api.inbox()]);
    if (!me.ok) {
      setState({ status: "error" });
      return;
    }
    setState({ status: "ready", me: me.data, inbox: inbox.ok ? inbox.data : { unread_count: 0, messages: [] } });
  }, []);

  useEffect(() => {
    if (ready && member) void load();
  }, [ready, member, load]);

  if (ready && !member) return <Navigate to={DASHBOARD_LOGIN} replace />;

  const setMe = (me: Me) => setState((s) => (s.status === "ready" ? { ...s, me } : s));
  const setInbox = (inbox: Inbox) => setState((s) => (s.status === "ready" ? { ...s, inbox } : s));

  return (
    <div className="min-h-screen overflow-x-hidden bg-ground font-sans text-chrome antialiased">
      <main className="px-4 pb-20 pt-24 md:px-6 md:pb-28 md:pt-32">
        <div className="mx-auto w-full max-w-[1240px]">
          {state.status === "error" && (
            <div data-testid="dashboard-error" className="rounded-2xl border border-ground-edge bg-ground-raised p-10 text-center">
              <p className="text-sm text-chrome-dim">{t.error}</p>
              <button
                type="button"
                onClick={load}
                className="bf-focusable mt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-money"
              >
                {t.retry}
              </button>
            </div>
          )}
          {state.status === "loading" && (
            <p className="py-20 text-center text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{t.loading}</p>
          )}
          {state.status === "ready" && (
            <Ready me={state.me} inbox={state.inbox} tab={tab} setTab={setTab} setMe={setMe} setInbox={setInbox} />
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Ready({
  me,
  inbox,
  tab,
  setTab,
  setMe,
  setInbox,
}: {
  me: Me;
  inbox: Inbox;
  tab: DashboardTab;
  setTab: (tab: DashboardTab) => void;
  setMe: (me: Me) => void;
  setInbox: (inbox: Inbox) => void;
}) {
  const t = copy.dashboard;
  const location = [me.city, me.state, me.country].filter((s) => s.trim() !== "").join(", ");
  const unread = inbox.messages.filter((m) => !m.is_read).length;
  const showRail = RAIL_TABS.includes(tab);

  return (
    <>
      {!me.email_verified && (
        <div
          data-testid="verify-banner"
          className="mb-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gold/40 bg-gold/10 px-5 py-4"
        >
          <p className="flex items-center gap-2 text-sm text-chrome">
            <Icon name="mail" className="text-base text-gold" />
            {t.verifyBanner}
          </p>
          {/* Go sends the email and redirects back; a full page load. */}
          <a
            href="/send-verification"
            className="bf-focusable whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.14em] text-gold hover:text-gold-light"
          >
            {t.resend}
          </a>
        </div>
      )}

      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-ground-edge pb-8 md:pb-10">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{t.eyebrow}</p>
          <h1 className="mt-3 break-words font-headline text-4xl uppercase leading-[0.95] tracking-[-0.02em] text-chrome md:text-5xl">
            {t.welcome} <span data-testid="dashboard-username" className="text-gold">{me.username}</span>
          </h1>
          {(location || me.desktop_key) && (
            <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.14em] text-chrome-dim">
              {location && (
                <span data-testid="dashboard-location" className="flex items-center gap-1.5">
                  <Icon name="mapPin" className="text-gold" />
                  {location}
                </span>
              )}
              {me.desktop_key && (
                <span data-testid="desktop-key" className="flex items-center gap-1.5 normal-case tracking-normal">
                  <Icon name="key" className="text-gold" />
                  {t.desktopKey}: <span className="select-all text-chrome">{me.desktop_key}</span>
                </span>
              )}
            </p>
          )}
        </div>
        {me.rank !== undefined && (
          <div data-testid="rank-card" className="rounded-2xl border border-ground-edge bg-ground-raised px-5 py-4 text-right">
            <p className="text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{t.ranking}</p>
            <p className="bf-tabular font-headline text-3xl leading-none text-chrome">#{me.rank}</p>
          </div>
        )}
      </header>

      <div className="mt-7">
        <DashboardTabs active={tab} onChange={setTab} unread={unread} />
      </div>

      <div className={`mt-7 grid grid-cols-1 gap-6 ${showRail ? "lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-8" : ""}`}>
        <div className="min-w-0" role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
          {tab === "profile" && <ProfileTab me={me} onSaved={setMe} />}
          {tab === "photo" && <PhotoTab me={me} onSaved={setMe} />}
          {tab === "inbox" && <InboxTab inbox={inbox} onChange={setInbox} />}
          {tab === "withdrawals" && <WithdrawalsTab paypalEmail={me.paypal_email} />}
        </div>
        {showRail && <Sidebar me={me} />}
      </div>
    </>
  );
}
